import {
  ConfigurationValues,
  getDefaultConfiguration,
} from '@cutr/constants/windows/windows';
import {
  BasicValue,
  CONFIGURATIONS_OPTIONS,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { Checkbox } from '@/primitives/Checkbox';
import { Label } from '@/windows/primitives/Label';
import { Select, SelectItem } from '@/windows/primitives/Select';

import { unboxLabel } from '../Configurations';
import { useConfigurations, useResolvedSystem } from '../ConfigurationsContext';
import { optionFilterBySystemAndClass } from '../inputs/ConfigurationSelect';
import { ConfigurationComponent } from '.';
import styles from './FrameBeamWidthConfig.module.css';

type FrameBeamSide = 'top' | 'left' | 'right' | 'bottom';

const SIDES: FrameBeamSide[] = ['top', 'left', 'right', 'bottom'];
const WIDTH_OPTIONS = CONFIGURATIONS_OPTIONS.frameBeamWidth.options;

const FrameBeamWidthInput = () => {
  const { t } = useTranslation();
  const { configurations, dispatch } = useConfigurations();
  const system = useResolvedSystem();
  const labelId = useId();
  const inputId = useId();
  const sidesLabelId = useId();

  const options = WIDTH_OPTIONS.filter((o) =>
    optionFilterBySystemAndClass(o, { system })
  );

  const systemDefault = getDefaultConfiguration('frameBeamWidth', system).top;
  const systemDefaultId = systemDefault.value;

  const current = configurations.frameBeamWidth;
  const [selectedWidth, setSelectedWidth] = useState<BasicValue>(
    () =>
      SIDES.map((side) => current?.[side]).find(
        (option) => option && option.value !== systemDefaultId
      ) ?? systemDefault
  );

  // A system change normalizes the store to system-valid widths; if the
  // remembered pick is no longer offered, fall back to the store/default so the
  // select and side checkboxes don't show a stale, out-of-system value.
  const effectiveWidth = options.some((o) => o.value === selectedWidth.value)
    ? selectedWidth
    : (SIDES.map((side) => current?.[side]).find(
        (option) => option && option.value !== systemDefaultId
      ) ?? systemDefault);

  const currentSides: Record<FrameBeamSide, boolean> = {
    top: (current?.top.value ?? systemDefaultId) !== systemDefaultId,
    left: (current?.left.value ?? systemDefaultId) !== systemDefaultId,
    right: (current?.right.value ?? systemDefaultId) !== systemDefaultId,
    bottom: (current?.bottom.value ?? systemDefaultId) !== systemDefaultId,
  };

  const dispatchChange = (
    newWidth: BasicValue,
    sides: Record<FrameBeamSide, boolean>
  ) => {
    dispatch({
      type: 'SET_FRAME_BEAM_WIDTH',
      value: {
        top: sides.top ? newWidth : systemDefault,
        left: sides.left ? newWidth : systemDefault,
        right: sides.right ? newWidth : systemDefault,
        bottom: sides.bottom ? newWidth : systemDefault,
      },
    });
  };

  return (
    <>
      <Label id={labelId} htmlFor={inputId}>
        {t('windows.configurations.frameBeamWidth.label')}
      </Label>
      <Select
        aria-labelledby={labelId}
        id={inputId}
        items={options}
        selectedKey={effectiveWidth.value!}
        onSelectionChange={(key) => {
          const newWidth = options.find((o) => o.value === key)!;
          setSelectedWidth(newWidth);
          dispatchChange(newWidth, currentSides);
        }}
      >
        {(option) => (
          <SelectItem id={option.value} textValue={unboxLabel(option.label)}>
            <span>{unboxLabel(option.label)}</span>
          </SelectItem>
        )}
      </Select>

      {effectiveWidth.value !== systemDefaultId && (
        <>
          <Label id={sidesLabelId}>
            {t('windows.configurations.frameBeamWidth.sides.label')}
          </Label>
          <div
            role="group"
            aria-labelledby={sidesLabelId}
            className={styles.sidesGroup}
          >
            {SIDES.map((side) => (
              <Checkbox
                key={side}
                checked={currentSides[side]}
                onChange={(e) => {
                  dispatchChange(effectiveWidth, {
                    ...currentSides,
                    [side]: e.target.checked,
                  });
                }}
              >
                {t(`windows.configurations.frameBeamWidth.sides.${side}`)}
              </Checkbox>
            ))}
          </div>
        </>
      )}
    </>
  );
};

// Sides are "active" when they differ from the system-specific default (66 on
// gracil, 81 on wood-alu), read from configuration context — not the global 78
// or the most-common side value, both of which mislabel customized sides.
const FrameBeamWidthDisplay = ({
  value,
}: {
  value: ConfigurationValues['frameBeamWidth'];
}) => {
  const system = useResolvedSystem();
  if (!value) return <span>-</span>;
  const systemDefaultId = getDefaultConfiguration('frameBeamWidth', system).top
    .value;
  const activeSides = SIDES.filter(
    (side) => value[side].value !== systemDefaultId
  );
  const width = activeSides.length > 0 ? value[activeSides[0]] : value.top;
  const sidesLabel =
    activeSides.length > 0
      ? ` (${activeSides
          .map((s) =>
            i18n.t(`windows.configurations.frameBeamWidth.sides.${s}`)
          )
          .join(', ')})`
      : '';
  return (
    <span>
      {unboxLabel(width.label)}
      {sidesLabel}
    </span>
  );
};

const config: ConfigurationComponent<'frameBeamWidth'> = {
  key: 'frameBeamWidth',
  input: <FrameBeamWidthInput />,
  display: (value: ConfigurationValues['frameBeamWidth']) => (
    <FrameBeamWidthDisplay value={value} />
  ),
};

export default config;
